import React from 'react';
import {
  Button,
  RequiredFieldIndicator,
  useModal,
  StatusBadge,
} from '@gsa/afp-component-library';
import {
  emDashUnicode,
  enDashUnicode,
} from '../../../../../utilities/constants';
import { useContractLine } from '../../provider/contract-line-provider';
import { getMakeModelStatusBadge } from '../../../../bid-dashboard/helpers';
import { SIMakeModelPanellProvider } from '../../../contract-line/make-model/si-make-model-panel-provider';
import SelectMakeModelModal from './select-make-model-modal';
import { getContractMakeModelApprovalStatus } from '../../../../make-model-approval/make-model-status-determination';

const LineMakeModelSection = () => {
  const {
    contractLine,
    lineTabData,
    setLineTabData,
    showChassis,
    requiresChassis,
    hasSinReqChassisTag,
    canUpdateContract,
  } = useContractLine();

  const [badge, setBadge] = React.useState(null);
  const { isOpen, openModal, closeModal } = useModal();

  const genLabel = (label, required) => (
    <span className="text-bold" data-testid={label}>
      {label} {required && <RequiredFieldIndicator />}
    </span>
  );

  const makeName =
    lineTabData?.make?.makeName ||
    lineTabData?.make?.bodyMakeInputValue ||
    contractLine?.make?.makeName ||
    enDashUnicode;
  const modelName =
    lineTabData?.model?.modelName ||
    lineTabData?.model?.bodyModelInputValue ||
    contractLine?.model?.modelName ||
    enDashUnicode;
  const chassisMakeName = lineTabData?.chassisMake
    ? lineTabData?.chassisMake?.makeName
    : contractLine?.chassisMake?.makeName;
  const chassisModelName = lineTabData?.chassisModel
    ? lineTabData?.chassisModel?.modelName
    : contractLine?.chassisModel?.modelName;
  const modelYear =
    lineTabData?.modelYear || contractLine?.modelYear || enDashUnicode;

  const makeModelItem = {
    makeModelApproval: {
      fleetApprovalStatus: contractLine?.makeModelApproval?.fleetApprovalStatus,
    },
    make: {
      fleetStatus:
        lineTabData?.make?.fleetStatus || contractLine?.make?.fleetStatus,
    },
    model: {
      fleetStatus:
        lineTabData?.model?.fleetStatus || contractLine?.model?.fleetStatus,
    },
  };

  const solicitation = contractLine?.contractHeader?.solicitation;
  const purchaseTypeCode = solicitation?.purchaseTypeCode;
  const isSOP = purchaseTypeCode === 'S';
  const isNonStandard = purchaseTypeCode === 'N';
  const isMas = purchaseTypeCode === 'M';
  const isAwardedInFleet = contractLine?.contractHeader?.fleetAwarded;

  const status = getContractMakeModelApprovalStatus(
    makeModelItem,
    hasSinReqChassisTag,
    isSOP,
    isAwardedInFleet,
  );

  const manufacturerModelNumber =
    lineTabData?.manufacturerModelNumber ||
    contractLine?.manufacturerModelNumber;
  const showManufacturerNumber =
    (isNonStandard && contractLine?.manufacturerModelNumber) || isMas;

  React.useEffect(() => {
    if (!showManufacturerNumber) setBadge(getMakeModelStatusBadge(status));
  }, [status, showManufacturerNumber]);

  const onSelectMakeModel = (data, isMakeModelChange) => {
    setLineTabData((prev) => ({ ...prev, ...data }));
    if (isMakeModelChange) setBadge(null);
    closeModal();
  };

  return (
    <div className="margin-top-2 margin-bottom-4">
      <div className="grid-row grid-gap-lg">
        <div className="grid-col flex-1">
          {genLabel('Standard item', false)}
          <div className="padding-top-1">
            {contractLine?.standardItem?.standardItemNumber ||
              enDashUnicode}{' '}
          </div>
        </div>

        {showManufacturerNumber && (
          <>
            <div className="grid-col flex-1">
              {genLabel('Manufacturer model number', false)}
              <div className="padding-top-1">
                {manufacturerModelNumber || enDashUnicode}
              </div>
            </div>
            <div className="grid-col flex-1">
              {genLabel('Model year', false)}
              <div className="padding-top-1">
                {parseInt(modelYear, 10) === 1901 ? emDashUnicode : modelYear}
              </div>
            </div>
          </>
        )}
        {!showManufacturerNumber && (
          <>
            <div className="grid-col flex-1">
              {genLabel('Body Make and Model', false)}
              <div className="padding-top-1">
                {makeName} {modelName}
              </div>
            </div>
            {showChassis && (
              <div className="grid-col flex-1">
                {genLabel('Chassis Make and Model', false)}
                <div className="padding-top-1">
                  {chassisMakeName || chassisModelName
                    ? `${chassisMakeName || ''} ${
                        chassisModelName || ''
                      }`.trim()
                    : emDashUnicode}
                </div>
              </div>
            )}
            <div className="grid-col flex-1">
              {genLabel('Model year', false)}
              <div className="padding-top-1">
                {parseInt(modelYear, 10) === 1901 ? emDashUnicode : modelYear}
              </div>
            </div>
            <div className="grid-col flex-1">
              {genLabel('Status', false)}
              <div className="padding-top-1">
                {badge ? (
                  <StatusBadge variant={badge.variant}>
                    {badge.label}
                  </StatusBadge>
                ) : (
                  emDashUnicode
                )}
              </div>
            </div>
          </>
        )}
        {canUpdateContract && (
          <div className="grid-col flex-1 padding-top-1">
            <Button
              className="flex-justify-self-end"
              variant="outline"
              label="Edit line item"
              onClick={openModal}
            />
          </div>
        )}
      </div>

      {isOpen && (
        <SIMakeModelPanellProvider
          showChassis={showChassis}
          requiresChassis={requiresChassis}
          initialState={{
            bodyMake: lineTabData?.make || contractLine?.make,
            bodyModel: lineTabData?.model || contractLine?.model,
            chassisMake: lineTabData?.chassisMake || contractLine?.chassisMake,
            chassisModel:
              lineTabData?.chassisModel || contractLine?.chassisModel,
            modelYear: lineTabData?.modelYear || contractLine?.modelYear,
            standardItem: contractLine?.standardItem,
          }}
        >
          <SelectMakeModelModal
            showManufacturerNumber={showManufacturerNumber}
            data={contractLine}
            onClose={closeModal}
            onSelect={onSelectMakeModel}
          />
        </SIMakeModelPanellProvider>
      )}
    </div>
  );
};

export default LineMakeModelSection;
